import { useState } from "react";
import { useTranslation } from "react-i18next";
import { kioskClient } from "../../lib/kiosk-client";
import type { FlowState } from "../../pages/FlowPage";
import { BigButton } from "../BigButton";

interface Props {
  state: FlowState;
  onDone: () => void;
}

export function ReceiptStep({ state, onDone }: Props) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function handleSend() {
    if (!state.order_id || !phone.trim()) return;
    setSending(true);
    setError("");
    try {
      await kioskClient.sendSmsReceipt(state.order_id, phone.trim());
      setSent(true);
    } catch {
      setError(t("error.networkBody"));
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center gap-8 h-full p-8">
        <p className="text-3xl font-bold text-green-600">
          {t("flow.receipt.sent")}
        </p>
        <BigButton variant="primary" onClick={onDone}>
          {t("flow.receipt.finish")}
        </BigButton>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-8 h-full">
      <h2 className="text-3xl font-bold text-center">
        {t("flow.receipt.title")}
      </h2>
      <label className="text-xl font-medium" htmlFor="phone-input">
        {t("flow.receipt.emailLabel")}
      </label>
      <input
        id="phone-input"
        type="tel"
        inputMode="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder={t("flow.receipt.phonePlaceholder")}
        className="w-full rounded-[var(--kiosk-brand-radius)] border-2 border-[var(--kiosk-brand-primary)] px-6 py-4 text-2xl focus:outline-none focus:border-[var(--kiosk-brand-accent)]"
        autoFocus
      />
      {error && (
        <p role="alert" className="text-red-600 text-lg text-center">
          {error}
        </p>
      )}
      <div className="flex gap-4 mt-auto">
        <BigButton
          variant="secondary"
          fullWidth
          onClick={onDone}
          disabled={sending}
        >
          {t("flow.receipt.skip")}
        </BigButton>
        <BigButton
          variant="primary"
          fullWidth
          onClick={() => void handleSend()}
          disabled={!phone.trim() || sending}
        >
          {sending ? t("flow.receipt.sending") : t("flow.receipt.send")}
        </BigButton>
      </div>
    </div>
  );
}
